// Type definitions for help content
export interface HelpFeature {
  text: string;
}

export interface HelpSection {
  title: string;
  items: string[];
}

export interface HelpContentItem {
  title: string;
  content: string;
  features?: HelpFeature[];
  featuresTitle?: string;
  sections?: HelpSection[];
  note?: string;
}

// Help content for each page in the application
export const helpContent: Record<string, HelpContentItem> = {
  '/qr/dash': {
    title: 'Dashboard Übersicht',
    content: 'Auf Ihrem persönlichen Dashboard verschaffen Sie sich einen vollständigen Überblick über all Ihre digitalen Buchmarketing-Aktivitäten, indem zentrale Kennzahlen und aktuelle Ereignisse elegant zusammengeführt werden. Diese Kommandozentrale ermöglicht es Ihnen, den Puls Ihrer Leserinteraktionen stets im Blick zu behalten und zeitnah auf Trends zu reagieren.',
    features: [
      { 
        text: 'Der prominente Button "+ Neuer QR-Code" führt Sie direkt zur Erstellung Ihrer nächsten digitalen Brücke zwischen Print und Online.'
      },
      { 
        text: 'Die Kacheln zeigen Ihre Gesamtstatistiken: wie viele QR-Codes Sie erstellt haben, wie oft diese gescannt wurden und welche Bücher aktiv sind.'
      },
      { 
        text: 'Der Bereich "Kürzliche Aktivitäten" dokumentiert die neuesten Scan-Ereignisse in Echtzeit, sodass Sie unmittelbar sehen, wann und wo Ihre Codes genutzt werden.'
      },
      { 
        text: 'Die Link-Statusprüfung verifiziert kontinuierlich die Erreichbarkeit Ihrer Zielseiten und warnt Sie bei Problemen.'
      }
    ]
  },
  
  '/qr/dash/links': {
    title: 'QR-Code Verwaltung',
    content: 'In der QR-Code-Verwaltung erschaffen und pflegen Sie die digitalen Portale, die Ihre gedruckten Werke mit der Online-Welt verbinden. Jeder Code ist ein Tor zu erweiterten Inhalten, die das Leseerlebnis bereichern und die Bindung zu Ihren Lesern vertiefen.',
    featuresTitle: 'Verfügbare QR-Code-Typen:',
    features: [
      { 
        text: 'Website-QR-Codes leiten Ihre Leser zu beliebigen Webseiten – perfekt für Autorenwebsites, Veranstaltungshinweise oder weiterführende Informationen.'
      },
      { 
        text: 'YouTube-Integration ermöglicht das Einbinden von Buchtrailern, Autorenlesungen oder Making-of-Videos direkt aus Ihrem Buch heraus.'
      },
      { 
        text: 'Spotify-Verknüpfungen schaffen eine auditive Dimension durch Hörbücher, thematisch passende Playlists oder Podcast-Episoden.'
      },
      { 
        text: 'PDF-Downloads bieten Bonusmaterial wie zusätzliche Kapitel, Charaktersteckbriefe oder exklusive Kurzgeschichten.'
      },
      { 
        text: 'Multi-Shop-Seiten präsentieren alle Bezugsquellen übersichtlich auf einer Seite – vom Online-Händler bis zur lokalen Buchhandlung.'
      },
      { 
        text: 'Autorenprofile zeigen Ihre persönliche Seite mit Biografie, Werkliste und Kontaktmöglichkeiten in ansprechender Form.'
      },
      { 
        text: 'Interaktive Buchproben mit optionaler E-Mail-Erfassung helfen beim Aufbau Ihrer Leser-Community.'
      }
    ],
    sections: [
      {
        title: 'So erstellen Sie einen QR-Code:',
        items: [
          'Klicken Sie auf "+ Neuer QR-Code" und wählen Sie den passenden Typ für Ihr Vorhaben',
          'Geben Sie einen eindeutigen Namen ein und füllen Sie die spezifischen Felder aus',
          'Personalisieren Sie optional das Erscheinungsbild mit Ihrem Branding',
          'Generieren Sie den Code und laden Sie ihn in druckfähiger Qualität herunter'
        ]
      }
    ]
  },
  
  '/qr/dash/content': {
    title: 'Inhalte verwalten',
    content: 'Die Inhaltsverwaltung bildet das Fundament Ihrer digitalen Buchmarketing-Strategie, indem sie alle Ihre Werke und Kampagnen an einem zentralen Ort organisiert. Eine durchdachte Strukturierung Ihrer Inhalte spart Zeit und schafft die Basis für erfolgreiche QR-Code-Kampagnen.',
    features: [
      { 
        text: 'Bücher bilden die Grundlage: Erfassen Sie Titel, ISBN und laden Sie ansprechende Cover hoch, die Ihre Werke optimal präsentieren.'
      },
      { 
        text: 'Kampagnen ermöglichen die thematische Bündelung von QR-Codes für Buchvorstellungen, Lesereisen oder saisonale Aktionen.'
      },
      { 
        text: 'Der Status-Manager unterscheidet zwischen Entwürfen in Arbeit, veröffentlichten Inhalten und archivierten Projekten.'
      }
    ],
    sections: [
      {
        title: 'Effektiver Arbeitsablauf:',
        items: [
          'Legen Sie zuerst Ihr Buch mit allen bibliografischen Daten an',
          'Laden Sie ein hochwertiges Cover hoch – es wird in vielen QR-Zielseiten verwendet',
          'Erstellen Sie QR-Codes direkt aus der Buchansicht heraus',
          'Nutzen Sie Kampagnen für zeitlich begrenzte Marketingaktionen'
        ]
      }
    ]
  },
  
  '/qr/dash/stats': {
    title: 'Statistik-Übersicht',
    content: 'Die Statistikseite verwandelt Rohdaten in wertvolle Erkenntnisse über das Verhalten Ihrer Leser und die Reichweite Ihrer QR-Codes. Diese Analysen helfen Ihnen zu verstehen, welche Inhalte resonieren und wo Optimierungspotential besteht.',
    features: [
      { 
        text: 'Der Scan-Verlauf visualisiert die Nutzung Ihrer QR-Codes über Zeit und enthüllt Muster in der Leseraktivität.'
      },
      { 
        text: 'Die geografische Verteilung zeigt, aus welchen Regionen Ihre Leser kommen – von lokaler bis internationaler Reichweite.'
      },
      { 
        text: 'Geräte- und Browser-Statistiken geben Aufschluss über die technischen Präferenzen Ihrer Zielgruppe.'
      },
      { 
        text: 'Die Tageszeit-Analyse verrät, wann Ihre Codes am häufigsten gescannt werden – wertvolles Wissen für zeitgesteuerte Kampagnen.'
      },
      { 
        text: 'Die Top-Links-Übersicht identifiziert Ihre erfolgreichsten Inhalte und zeigt, was bei Lesern besonders gut ankommt.'
      }
    ],
    note: 'Hinweis: Vollständige Statistiken stehen ab dem Starter-Plan zur Verfügung. Im Free-Plan sehen Sie eine Vorschau der Möglichkeiten.'
  },
  
  '/qr/dash/reports': {
    title: 'Berichte erstellen',
    content: 'Mit der Berichtsfunktion erstellen Sie professionelle Dokumentationen Ihrer QR-Code-Performance für Präsentationen, Verlagsgespräche oder Ihre eigene Erfolgskontrolle. Gut aufbereitete Daten untermauern Ihre Marketingentscheidungen mit Fakten.',
    features: [
      { 
        text: 'Flexible Zeiträume ermöglichen Analysen von Tagesperformance bis zu Jahresübersichten – ganz nach Ihrem Bedarf.'
      },
      { 
        text: 'Der Übersichtsbericht fasst alle wichtigen Kennzahlen kompakt zusammen – ideal für schnelle Einblicke.'
      },
      { 
        text: 'Detailberichte tauchen tief in die Performance einzelner QR-Codes ein und zeigen granulare Nutzungsmuster.'
      },
      { 
        text: 'Export-Funktionen in PDF für Präsentationen oder CSV für weitere Datenanalyse in Tabellenkalkulationen.'
      },
      { 
        text: 'Die Länderstatistik visualisiert Ihre internationale Reichweite und hilft bei der geografischen Zielgruppenanalyse.'
      }
    ],
    note: 'Hinweis: Die Berichtsfunktion steht ab dem Starter-Plan zur Verfügung und erweitert sich mit höheren Plänen um zusätzliche Analysemöglichkeiten.'
  },
  
  '/qr/dash/team': {
    title: 'Team-Verwaltung',
    content: 'Die Team-Funktionalität transformiert BeyondPages von einem Einzelwerkzeug in eine kollaborative Plattform, auf der Autoren, Lektoren und Marketing-Experten gemeinsam an erfolgreichen Buchkampagnen arbeiten. Durchdachte Rechtevergabe sorgt dabei für Sicherheit und Effizienz.',
    featuresTitle: 'Die Rollenhierarchie im Detail:',
    features: [
      { 
        text: 'Eigentümer besitzen vollständige Kontrolle über Account, Abrechnung und können das gesamte Team verwalten.'
      },
      { 
        text: 'Administratoren verwalten Inhalte und Teammitglieder, haben jedoch keinen Zugriff auf Abrechnungsdaten.'
      },
      { 
        text: 'Mitglieder arbeiten mit den ihnen zugewiesenen Büchern und Kampagnen – ideal für externe Unterstützung.'
      }
    ],
    sections: [
      {
        title: 'Team aufbauen und verwalten:',
        items: [
          'Laden Sie neue Mitglieder per E-Mail ein und wählen Sie die passende Rolle',
          'Vergeben Sie spezifische Berechtigungen für einzelne Bücher oder Kampagnen',
          'Überwachen Sie Team-Aktivitäten im Aktivitätsprotokoll',
          'Passen Sie Berechtigungen flexibel an sich ändernde Projektanforderungen an'
        ]
      }
    ]
  },
  
  '/qr/dash/profile': {
    title: 'Konto-Einstellungen',
    content: 'In den Kontoeinstellungen formen Sie die digitale Identität Ihrer Autorenpräsenz und verwalten die administrativen Aspekte Ihres Accounts. Jede hier getroffene Entscheidung prägt, wie Ihre Leser Sie online wahrnehmen.',
    features: [
      { 
        text: 'Die Kontoverwaltung umfasst Ihre persönlichen Daten und die zentrale E-Mail-Adresse für alle Benachrichtigungen.'
      },
      { 
        text: 'Das Abonnement-Dashboard zeigt Ihren aktuellen Plan, verbleibende Kontingente und ermöglicht flexibles Up- oder Downgrade.'
      },
      { 
        text: 'URL-Einstellungen definieren Ihren individuellen Kurz-Link (beyondpages.link/IhrName) für professionelle, merkbare Adressen.'
      },
      { 
        text: 'Das Branding-Center lässt Sie Logo, Farbschema und Darstellungsoptionen festlegen – Ihre visuelle Handschrift im Netz.'
      },
      { 
        text: 'Die Gefahrenzone enthält unwiderrufliche Aktionen wie Kontolöschung – mit mehrfachen Sicherheitsabfragen geschützt.'
      }
    ],
    note: 'Wichtig: Änderungen am Branding wirken sich sofort auf alle Ihre bestehenden QR-Zielseiten aus. Prüfen Sie Anpassungen daher sorgfältig in der Vorschau.'
  },
  
  '/qr/dash/ai-ideas': {
    title: 'KI-Ideen Generator',
    content: 'Der KI-Assistent analysiert Ihre Buchprojekte mit künstlicher Intelligenz und generiert maßgeschneiderte Vorschläge für QR-Code-Inhalte. Diese kreative Unterstützung hilft Ihnen, innovative Wege der Leserinteraktion zu entdecken, die Sie vielleicht selbst nicht bedacht hätten.',
    features: [
      { 
        text: 'Die intelligente Buchanalyse erkennt Genre, Themen und Zielgruppe automatisch aus Ihren eingegebenen Informationen.'
      },
      { 
        text: 'Kapitelspezifische Vorschläge bieten punktgenaue Ideen für einzelne Abschnitte Ihres Werks.'
      },
      { 
        text: 'Konkrete Umsetzungstipps verwandeln abstrakte Ideen in praktisch realisierbare QR-Code-Projekte.'
      },
      { 
        text: 'Die Team-Sharing-Funktion ermöglicht es, generierte Ideen mit Kollegen zu teilen und gemeinsam weiterzuentwickeln.'
      }
    ],
    sections: [
      {
        title: 'Monatliche Kontingente:',
        items: [
          'Free-Plan: 5 KI-Anfragen für erste Experimente',
          'Starter-Plan: 20 Anfragen für regelmäßige Nutzung',
          'Growth-Plan: 50 Anfragen für intensive Kampagnenplanung',
          'Business-Plan: Unbegrenzte Anfragen für maximale Kreativität'
        ]
      }
    ],
    note: 'Die Kontingente setzen sich jeden Monat automatisch zurück. Nicht genutzte Anfragen verfallen.'
  },
  
  '/qr/dash/campaigns': {
    title: 'Kampagnen-Management',
    content: 'Kampagnen sind Ihr Werkzeug für orchestrierte Marketingaktionen, bei denen mehrere QR-Codes synergetisch zusammenwirken. Diese Bündelung ermöglicht es Ihnen, komplexe Marketingstrategien übersichtlich zu verwalten und deren Erfolg gebündelt zu messen.',
    features: [
      { 
        text: 'Die Kampagnengruppierung fasst thematisch verwandte QR-Codes zusammen – etwa alle Codes einer Buchvorstellungstour.'
      },
      { 
        text: 'Zeitliche Begrenzungen mit Start- und Enddatum eignen sich perfekt für saisonale Aktionen oder befristete Angebote.'
      },
      { 
        text: 'Ausführliche Beschreibungen dokumentieren Strategie und Ziele für alle Beteiligten nachvollziehbar.'
      },
      { 
        text: 'Gebündelte Performance-Metriken zeigen den Gesamterfolg der Kampagne über alle zugeordneten QR-Codes.'
      }
    ],
    sections: [
      {
        title: 'Kampagnen effektiv einsetzen:',
        items: [
          'Erstellen Sie eine Kampagne mit aussagekräftigem Namen und klarer Zielsetzung',
          'Definieren Sie den Aktionszeitraum passend zu Ihren Marketingplänen',
          'Ordnen Sie relevante QR-Codes der Kampagne zu',
          'Verfolgen Sie den Erfolg durch die aggregierten Statistiken'
        ]
      }
    ]
  }
};

// Get help content for current path
export const getHelpContent = (pathname: string): HelpContentItem => {
  // Try exact match first
  if (helpContent[pathname]) {
    return helpContent[pathname];
  }
  
  // Try to match parent paths for nested routes
  const pathParts = pathname.split('/');
  while (pathParts.length > 0) {
    const parentPath = pathParts.join('/');
    if (helpContent[parentPath]) {
      return helpContent[parentPath];
    }
    pathParts.pop();
  }
  
  // Default help content
  return {
    title: 'Hilfe',
    content: 'Wählen Sie einen Bereich aus dem Menü, um kontextbezogene Hilfe zu erhalten. Jede Seite bietet spezifische Unterstützung für Ihre Aufgaben im digitalen Buchmarketing.'
  };
};